import { useState } from 'react'
import { tagForDevice } from '@kronos/domain'
import type { MachineKind } from '@kronos/domain'
import type { TailnetDevice } from '../../data'
import { AddMachine } from './AddMachine'
import type { View } from './derive'
import { ago } from './format'
import { NetDot } from './parts'

/** A device's own name as a starting name: `nupeak-r2` → "R2", `office-3090` → "Office 3090". */
function guess(d: TailnetDevice): { name: string; kind: MachineKind } {
  const robot = /^nupeak-r(\d+)$/i.exec(d.name)
  if (robot) return { name: `R${robot[1]}`, kind: 'robot' }
  return { name: d.name.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), kind: 'computer' }
}

/**
 * Tagged devices already on the network that Kronos does not manage yet — on
 * the day Kronos takes over, that is every one of them. One press makes it a
 * machine with its tag, so access to it is decided here from then on.
 */
export function FoundOnNetwork({ view }: { view: View }): JSX.Element | null {
  const [adding, setAdding] = useState<TailnetDevice | null>(null)
  if (view.unclaimed.length === 0) return null
  return (
    <section className="cx-panel cx-panel--padded cx-found" aria-label="Found on the network">
      <div className="stack" style={{ gap: 2 }}>
        <h2 className="cx-panel__title">Found on the network · {view.unclaimed.length}</h2>
        <span className="muted cx-small">Already joined, not managed here yet. Add one and its access is decided in Kronos.</span>
      </div>
      <ul className="cx-list">
        {view.unclaimed.map((d) => (
          <li key={d.id} className="cx-list__row cx-found__row">
            <span className="row" style={{ gap: 10, minWidth: 0 }}>
              <NetDot state={d.online ? 'online' : 'offline'} />
              <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                <span className="cx-mono cx-strong">{d.name}</span>
                <span className="muted cx-small">
                  {d.tags.join(', ')} · {d.addresses[0] ?? ''} · {d.online ? 'online' : d.lastSeen ? `seen ${ago(d.lastSeen)}` : 'offline'}
                </span>
              </span>
            </span>
            <button className="primary" onClick={() => setAdding(d)}>Add as machine</button>
          </li>
        ))}
      </ul>
      {adding ? (
        <AddMachine
          robots={view.page.robots}
          taken={view.page.world.machines.map((m) => m.tag)}
          initial={(() => {
            const own = tagForDevice(adding, view.page.devices.filter((d) => d.id !== adding.id))
            return { ...guess(adding), tag: own.tag, deviceId: adding.id, device: adding.name, keeps: adding.tags, added: own.added }
          })()}
          onClose={() => setAdding(null)}
        />
      ) : null}
    </section>
  )
}
